import { useEffect, useRef, useState } from "react";
import { X, Search, Users } from "lucide-react";
import { listWorkerProfiles } from "../workerProfiles/workerProfilesApi.js";
import Pagination from "../components/Pagination.jsx";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

/**
 * P145: the workers under one Agent, shown in place rather than by sending
 * the operator to the Worker Profiles screen in the left menu.
 *
 * Navigating away was the original design and it was wrong: the operator is
 * mid-task in an Agent's drawer, and landing on a different top-level page
 * (with its own filters, and no obvious way back) reads as "something went
 * wrong" rather than "here is the answer". A dialog keeps the Agent context
 * on screen behind it and closing returns exactly where they were.
 *
 * Reads the real worker-profiles list API filtered by this agentId - the
 * same endpoint and the same `agentId` filter the Worker Profiles page
 * itself uses, so there is no second definition of "this Agent's workers".
 */
export default function AgentWorkersDialog({ open, agentId, agentDisplayName, onClose }) {
  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ status: "idle", items: [], totalCount: 0, error: null });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset per-open so reopening for a different Agent never shows the
  // previous one's rows for a frame.
  useEffect(() => {
    if (!open) {
      setSearchInput("");
      setSearch("");
      setPage(1);
      setState({ status: "idle", items: [], totalCount: 0, error: null });
    }
  }, [open]);

  useEffect(() => {
    if (!open || !agentId) {
      return undefined;
    }
    let cancelled = false;
    setState((previous) => ({ ...previous, status: "loading", error: null }));

    listWorkerProfiles({ agentId, page, pageSize: PAGE_SIZE, search })
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", items: data?.items ?? [], totalCount: data?.totalCount ?? 0, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", items: [], totalCount: 0, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, agentId, page, search]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    inputRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(state.totalCount / PAGE_SIZE));

  return (
    <div
      className="wx-wl-dialog-root"
      onMouseDown={(event) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target)) {
          onClose();
        }
      }}
    >
      <div className="wx-wl-dialog-backdrop" aria-hidden="true" />
      <div ref={dialogRef} className="wx-wl-dialog" role="dialog" aria-modal="true" aria-label="แรงงานในสายงานของ Agent">
        <div className="wx-wl-dialog-head">
          <div>
            <h2>
              <Users size={16} aria-hidden="true" /> แรงงานของ {agentDisplayName || "Agent รายนี้"}
            </h2>
            <p>{state.status === "ready" ? `${state.totalCount.toLocaleString("en-US")} คน` : "กำลังโหลด..."}</p>
          </div>
          <button type="button" className="wx-wl-dialog-close" onClick={onClose} aria-label="ปิด">
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <label className="wx-wl-dialog-search">
          <Search size={15} aria-hidden="true" />
          <span className="wx-visually-hidden">ค้นหาชื่อหรือรหัสแรงงาน</span>
          <input
            ref={inputRef}
            type="search"
            placeholder="ค้นหาชื่อหรือรหัสแรงงาน..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>

        <div className="wx-wl-dialog-body">
          {state.status === "loading" && state.items.length === 0 && <p className="wx-wl-dialog-state">กำลังโหลดรายชื่อแรงงาน...</p>}

          {state.status === "error" && (
            <p className="wx-wl-dialog-state wx-wl-dialog-state--error">
              {state.error?.status === 403 ? "คุณไม่มีสิทธิ์ดูรายชื่อแรงงานของ Agent รายนี้" : "โหลดรายชื่อแรงงานไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"}
            </p>
          )}

          {state.status === "ready" && state.items.length === 0 && (
            <p className="wx-wl-dialog-state">{search ? "ไม่พบแรงงานที่ตรงกับคำค้นหา" : "Agent รายนี้ยังไม่มีแรงงานในสายงาน"}</p>
          )}

          {state.items.length > 0 && (
            <ul className="wx-wl-worker-list">
              {state.items.map((item) => {
                const fullName = item.worker?.fullNameNative || item.worker?.fullNameEnglish || item.workerCode;
                return (
                  <li className="wx-wl-worker-row" key={item.workerId}>
                    <div className="wx-wl-worker-row-main">
                      <strong>{fullName}</strong>
                      <span>{item.workerCode}</span>
                    </div>
                    <div className="wx-wl-worker-row-meta">
                      <span>{item.worker?.nationality ?? "-"}</span>
                      <span>{item.worker?.phoneNumber ?? "-"}</span>
                    </div>
                    <span className={`wx-badge wx-badge--${item.readyForMatch ? "success" : "neutral"}`}>{item.workerStatus}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {state.totalCount > PAGE_SIZE && (
          <Pagination page={page} totalPages={totalPages} totalCount={state.totalCount} pageSize={PAGE_SIZE} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}
